import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useWallet } from "@/contexts/WalletContext";
import { useIsAdmin } from "@/hooks/use-admin";
import { Loader2, Shield, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireModerator?: boolean;
  redirectTo?: string;
}

export function ProtectedRoute({ 
  children, 
  requireAdmin = false,
  requireModerator = false,
  redirectTo = "/" 
}: ProtectedRouteProps) {
  const { connected, connect } = useWallet();
  const { isAdmin, isLoading } = useIsAdmin();

  // Show loading while checking admin status
  if (requireAdmin && isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  // If not connected, show connect prompt
  if (!connected) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8 rounded-xl bg-card border border-border max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Wallet className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-xl font-display font-bold text-foreground mb-2">
            Wallet Required
          </h2>
          <p className="text-muted-foreground mb-6">
            Please connect your wallet to access this page.
          </p>
          <Button variant="hero" onClick={connect}>
            <Wallet className="w-4 h-4" />
            Connect Wallet
          </Button>
        </div>
      </div>
    );
  }

  // If admin required but user is not admin
  if (requireAdmin && !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8 rounded-xl bg-card border border-destructive/50 max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-display font-bold text-foreground mb-2">
            Access Denied
          </h2>
          <p className="text-muted-foreground mb-6">
            You do not have permission to access this page. Admin privileges required.
          </p>
          <Button variant="outline" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
